import EnIcon from "@/assets/icons/uk_flag.png";
import ViIcon from "@/assets/icons/vi_flag.png";
import { ELanguage, SpeechRecognitionErrorEvent, SpeechRecognitionEvent } from "@/common/types/build_idea";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";
import { alpha, Box, Button, CircularProgress, TextField, Tooltip, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import React, { useEffect, useRef, useState } from "react";

interface VoiceToTextProps {
  onTextChange?: (text: string) => void;
  onTextSubmit?: (text: string) => void;
  defaultValue?: string;
  placeholder?: string;
  isLoading?: boolean;
  compact?: boolean;
  hideSubmitActions?: boolean;
  autoFocus?: boolean;
  fontSize?: string;
  highlightMic?: boolean;
}

const pulseAnimation = `
  @keyframes pulseInner {
    0% {
      background-color: rgba(42, 157, 144, 0.1);
    }
    50% {
      background-color: rgba(42, 157, 144, 0.3);
    }
    100% {
      background-color: rgba(42, 157, 144, 0.1);
    }
  }

  @keyframes micPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

// Thêm style vào head của document
const style = document.createElement("style");
style.innerHTML = pulseAnimation;
document.head.appendChild(style);

const VoiceToText: React.FC<VoiceToTextProps> = ({
  onTextChange,
  onTextSubmit,
  defaultValue,
  placeholder,
  isLoading,
  compact = false,
  hideSubmitActions = false,
  autoFocus = false,
  fontSize,
  highlightMic = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [isListening, setIsListening] = useState<boolean>(false);
  const [text, setText] = useState<string>(defaultValue ?? "");
  // const [cursorPosition, setCursorPosition] = useState<number>(0);
  const cursorPositionRef = useRef<number>(0);
  const recognitionRef = useRef<Window["SpeechRecognition"] | null>(null);
  const [language, setLanguage] = useState<ELanguage | null>(null);

  const handleEventRecognition = (lang: ELanguage) => {
    if (isListening && lang === language) {
      handleStop();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Trình duyệt của bạn không hỗ trợ nhận diện giọng nói.");
      return;
    }
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.lang = lang === ELanguage.en ? "en-US" : "vi-VN";
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;

    recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = "";
      // let transcript = text ? (cursorPositionRef.current === 0 ? text + " " : text) : "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      const valText = text
        ? text.slice(0, cursorPositionRef.current) +
          " " +
          transcript +
          text.slice(cursorPositionRef.current, text.length)
        : transcript;
      setText(valText);
      if (onTextChange) onTextChange(valText);
    };

    recognitionRef.current.onerror = (event: SpeechRecognitionErrorEvent) => {
      alert(event.error);
      setIsListening(false);
    };

    setTimeout(() => {
      setLanguage(lang);
      handleStart();
    }, 100);
  };

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  const handleStart = () => {
    recognitionRef.current?.start();
    setIsListening(true);
  };

  const handleStop = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
    setLanguage(null);
  };

  // Note: defaultValue is intentionally used only as initial state (in useState above).
  // We do NOT sync defaultValue changes back into text via useEffect to avoid
  // re-render loops that cause typing lag when the parent re-renders on every keystroke.

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <TextField
          autoFocus={autoFocus}
          multiline
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (onTextChange && compact) onTextChange(e.target.value);
            isListening && handleStop();
          }}
          onKeyUp={(e) => {
            const target = e.target as HTMLInputElement;
            cursorPositionRef.current = target.selectionStart || 0;
          }}
          onMouseUp={(e) => {
            const target = e.target as HTMLInputElement;
            cursorPositionRef.current = target.selectionStart || 0;
          }}
          onKeyDown={
            compact
              ? (e) => {
                  // Handle Enter key to submit text
                  if (e.key === "Enter" && !e.shiftKey && onTextSubmit) {
                    e.preventDefault();
                    onTextSubmit(text);
                    setText("");
                  }
                }
              : undefined
          }
          fullWidth
          sx={{
            minWidth: compact ? "200px" : "350px",
            "& .MuiOutlinedInput-root": {
              fontSize: fontSize ? fontSize : compact ? "14px" : "16px",
              borderRadius: compact ? "12px" : "16px",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "#F4F6F8",
              minHeight: compact ? "100px" : "120px",
              padding: compact ? "12px 16px" : "16px 20px",
              transition: "all 0.2s ease-in-out",
              border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(145, 158, 171, 0.2)"}`,
              "& fieldset": {
                border: "none",
              },
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#FFFFFF",
                boxShadow: `0 0 0 2px ${alpha(isDark ? "#fff" : "#2a9d90", 0.1)}`,
              },
              "&.Mui-focused": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#FFFFFF",
                boxShadow: `0 0 0 2px ${alpha(isDark ? "#fff" : "#2a9d90", 0.2)}`,
              },
              "& textarea": {
                fontSize: fontSize ? fontSize : compact ? "14px" : "16px",
                lineHeight: "1.5",
                color: isDark ? "#fff" : "#212B36",
                "&::placeholder": {
                  color: isDark ? "rgba(255, 255, 255, 0.48)" : "#919EAB",
                  opacity: 1,
                  fontSize: fontSize ? fontSize : compact ? "13px" : "15px",
                },
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "0",
            },
          }}
          placeholder={placeholder}
        />
      </Box>
      <Box
        sx={{
          marginTop: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            position: "relative",
            alignItems: "center",
            flexWrap: "wrap",
            gap: compact ? "8px" : "12px",
            margin: "4px 0",
            "& .btn": {
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: "8px",
              padding: "6px 16px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(145, 158, 171, 0.32)"}`,
              position: "relative",
              borderRadius: "20px",
              transition: "all 0.3s ease",
              overflow: "hidden",
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
              minWidth: "auto",
              justifyContent: "center",
              "&:hover": {
                borderColor: "#2a9d90",
                backgroundColor: isDark ? "rgba(42, 157, 144, 0.1)" : "#f8fdfc",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              },
            },
            "& img": {
              width: "20px",
              height: "20px",
              objectFit: "contain",
              borderRadius: "50%",
            },
            "& svg": {
              fontSize: "18px",
              color: isDark ? "#919EAB" : grey[600],
              transition: "all 0.3s ease",
            },
            "& .btn-text": {
              fontSize: "13px",
              fontWeight: 700,
              color: isDark ? "#fff" : "#212B36",
              transition: "all 0.3s ease",
              display: "none", // Only icons for flags as in screenshot
            },
          }}
        >
          {/* Floating Tooltip Bubble pointing directly at the 2 mic buttons */}
          {highlightMic && !isListening && text.trim() === "" && (
            <Box
              sx={{
                position: "absolute",
                bottom: "calc(100% + 8px)",
                left: 0,
                background: "linear-gradient(135deg, #00B8D9 0%, #008CA8 100%)",
                color: "#FFFFFF",
                px: 1.2,
                py: 0.4,
                borderRadius: "8px",
                fontSize: "11px",
                fontWeight: 800,
                whiteSpace: "nowrap",
                boxShadow: "0 4px 14px rgba(0, 184, 217, 0.55)",
                animation: "bounceTooltip 1.6s infinite ease-in-out",
                pointerEvents: "none",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "100%",
                  left: "28px",
                  border: "5px solid transparent",
                  borderTopColor: "#008CA8",
                },
                "@keyframes bounceTooltip": {
                  "0%, 100%": { transform: "translateY(0)" },
                  "50%": { transform: "translateY(-4px)" },
                },
              }}
            >
              🎙️ Bạn bấm vào đây để nói tiếng Anh hoặc tiếng Việt nhé
            </Box>
          )}

          <Tooltip
            title={
              isListening && language === ELanguage.en
                ? "Dừng ghi âm tiếng Anh"
                : "Bấm vào đây để bắt đầu nói câu trả lời bằng tiếng Anh (English)"
            }
            arrow
            placement="top"
          >
            <Box
              className="btn"
              sx={{
                ...(highlightMic && !isListening
                  ? {
                      borderColor: "#00B8D9 !important",
                      borderWidth: "2px !important",
                      backgroundColor: isDark
                        ? "rgba(0, 184, 217, 0.15) !important"
                        : "rgba(0, 184, 217, 0.1) !important",
                      boxShadow: "0 0 16px 2px rgba(0, 184, 217, 0.7), 0 0 4px #00B8D9 !important",
                      animation: "pulseMicBtn 1.6s infinite ease-in-out !important",
                      "@keyframes pulseMicBtn": {
                        "0%, 100%": {
                          transform: "scale(1)",
                          boxShadow: "0 0 10px 1px rgba(0, 184, 217, 0.5)",
                        },
                        "50%": {
                          transform: "scale(1.06)",
                          boxShadow: "0 0 22px 4px rgba(0, 184, 217, 0.9)",
                        },
                      },
                    }
                  : {}),
                ...(language === ELanguage.en
                  ? {
                      backgroundColor: "#2a9d90 !important",
                      borderColor: "#2a9d90 !important",
                      color: "white !important",
                      "& .btn-text": {
                        color: "white !important",
                      },
                      "& svg": {
                        color: "white !important",
                      },
                    }
                  : {}),
                ...(isListening && language === ELanguage.en
                  ? {
                      animation: "pulseInner 2s infinite",
                      "& .mic-icon": {
                        color: "white !important",
                        animation: "micPulse 1s infinite",
                      },
                    }
                  : {}),
              }}
              onClick={() => {
                if (isListening && ELanguage.en !== language) {
                  recognitionRef.current?.stop();
                  setTimeout(() => {
                    handleEventRecognition(ELanguage.en);
                  }, 100);
                  return;
                } else {
                  handleEventRecognition(ELanguage.en);
                }
              }}
            >
              <img src={EnIcon} alt="English" />
              <span className="btn-text">{isListening && language === ELanguage.en ? "Stop" : "English"}</span>
              <MicIcon className="mic-icon" />
            </Box>
          </Tooltip>

          <Tooltip
            title={
              isListening && language === ELanguage.vi
                ? "Dừng ghi âm tiếng Việt"
                : "Bấm vào đây để bắt đầu nói câu trả lời bằng tiếng Việt"
            }
            arrow
            placement="top"
          >
            <Box
              className="btn"
              sx={{
                ...(highlightMic && !isListening
                  ? {
                      borderColor: "#00B8D9 !important",
                      borderWidth: "2px !important",
                      backgroundColor: isDark
                        ? "rgba(0, 184, 217, 0.15) !important"
                        : "rgba(0, 184, 217, 0.1) !important",
                      boxShadow: "0 0 16px 2px rgba(0, 184, 217, 0.7), 0 0 4px #00B8D9 !important",
                      animation: "pulseMicBtn 1.6s infinite ease-in-out !important",
                      "@keyframes pulseMicBtn": {
                        "0%, 100%": {
                          transform: "scale(1)",
                          boxShadow: "0 0 10px 1px rgba(0, 184, 217, 0.5)",
                        },
                        "50%": {
                          transform: "scale(1.06)",
                          boxShadow: "0 0 22px 4px rgba(0, 184, 217, 0.9)",
                        },
                      },
                    }
                  : {}),
                ...(language === ELanguage.vi
                  ? {
                      backgroundColor: "#2a9d90 !important",
                      borderColor: "#2a9d90 !important",
                      color: "white !important",
                      "& .btn-text": {
                        color: "white !important",
                      },
                      "& svg": {
                        color: "white !important",
                      },
                    }
                  : {}),
                ...(isListening && language === ELanguage.vi
                  ? {
                      animation: "pulseInner 2s infinite",
                      "& .mic-icon": {
                        color: "white !important",
                        animation: "micPulse 1s infinite",
                      },
                    }
                  : {}),
              }}
              onClick={() => {
                if (isListening && ELanguage.vi !== language) {
                  recognitionRef.current?.stop();
                  setTimeout(() => {
                    handleEventRecognition(ELanguage.vi);
                  }, 100);
                  return;
                } else {
                  handleEventRecognition(ELanguage.vi);
                }
              }}
            >
              <img src={ViIcon} alt="Tiếng Việt" />
              <span className="btn-text">{isListening && language === ELanguage.vi ? "Dừng" : "Tiếng Việt"}</span>
              <MicIcon className="mic-icon" />
            </Box>
          </Tooltip>
        </Box>
        {!hideSubmitActions && onTextSubmit && text.trim() !== "" ? (
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              "& button": {
                textTransform: "none",
                fontSize: compact ? "12px" : "14px",
                padding: compact ? "4px 10px" : "6px 16px",
                minWidth: "auto",
                borderRadius: "10px",
              },
            }}
          >
            <Button
              onClick={() => {
                handleStop();
                setTimeout(() => {
                  setText("");
                }, 10);
              }}
              variant="outlined"
              color="error"
              disabled={isLoading}
            >
              <CloseIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Clear
            </Button>
            <Button
              onClick={() => {
                onTextSubmit(text);
                setText("");
                handleStop();
              }}
              disabled={isLoading}
              variant="contained"
              startIcon={
                isLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />
              }
            >
              Done
            </Button>
          </Box>
        ) : (
          <Box />
        )}
      </Box>
    </Box>
  );
};

export default VoiceToText;
